/**
 * TrackBee's visitor id and the shopper's consent, read on the storefront and carried along with
 * the handoff (D367, provisional; `docs/TRACKBEE.md`).
 *
 * Runs on the merchant's own domain, inside the customer's browser. Nothing here may import from
 * `@/` — the bundle is self-contained (`docs/STOREFRONT.md`).
 *
 * ## Why here
 *
 * TrackBee's Tracking SDK on the Peak checkout runs in `url` identity mode: "The shopper reaches
 * your checkout from the Shopify storefront, and the visitor ID was already generated there. Your
 * job is to receive it, not to make a new one. … So have the storefront put `_t_i` and `_t_i_ts`
 * on the link through to your checkout" — and "The TrackBee Shopify app keeps the visitor ID in
 * the storefront's local storage under `_t_i`, and the time it was created under
 * `_t_i_timestamp`. Read both on the storefront and add them to the link to your checkout"
 * (https://docs.trackbee.ai/docs/tracking-sdk/install#where-the-visitor-id-comes-from, read
 * 2026-10-30). The checkout runs on another origin and can never read that storage, so — exactly
 * like the GA4 cookies (`./ga-cookies.ts`) — the values are read here, at the moment the shopper
 * leaves the shop, ride in the create payload as the top-level `trackbee` member, and are stored on
 * the `Checkout`; the service then builds the checkout's URL with `_t_i` and `_t_i_ts` (renamed
 * from `_t_i_timestamp`, as the docs warn) and keeps them on the confirmation page's URL too.
 *
 * Only when the storefront config says the store has TrackBee on (`config.trackbee`): a store
 * without it never has its shoppers' TrackBee ids sent to Peak. The server checks the same switch
 * again before it stores anything (a cached config can lag by up to a minute).
 *
 * ## Consent — Shopify's Customer Privacy API, read, never written
 *
 * TrackBee's `consent({ userData, personalization })` takes the visitor's choice; the docs map a
 * banner's "analytics" to `userData` and "marketing" to `personalization`
 * (https://docs.trackbee.ai/docs/tracking-sdk/consent#wiring-it-to-a-consent-banner). On a Shopify
 * storefront that choice is Shopify's Customer Privacy API (https://shopify.dev/docs/api/customer-privacy,
 * the methods `./experiment.ts` and `./url-click-ids.ts` already quote):
 * `analyticsProcessingAllowed()` → `userData`, `marketingAllowed()` → `personalization`. Asked
 * synchronously at handoff, and only a method that is on the page is asked: a method that is
 * missing (no API, or the loader still running) leaves its key out, so the checkout never invents
 * a choice; a method that throws counts as `false` ("a consent tool that throws is no licence").
 * `setTrackingConsent` is never called.
 *
 * ## Hygiene
 *
 * Exactly two `localStorage` keys are read and nothing is written. Every access is in a
 * `try`/`catch` — Safari private mode and a restrictive storage policy both throw — and a value
 * that does not fit is dropped, never sent: `_t_i` must be a UUID ("In `url` mode, whatever you
 * pass in `_t_i` has to be a UUID"), and `_t_i_timestamp` a string of at most 20 digits (TrackBee
 * does not document its format; D367 records that as unverified). Nothing is logged.
 */

/** TrackBee's Shopify app's storage key for the visitor id. */
export const TRACKBEE_STORAGE_VISITOR_KEY = '_t_i';

/** …and for the time it was created. Sent as `visitorTs`, put on the URL as `_t_i_ts`. */
export const TRACKBEE_STORAGE_TS_KEY = '_t_i_timestamp';

/** A UUID, any version, either case — the same rule as `src/lib/checkout/trackbee.ts`. */
export const TRACKBEE_UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Digits only, at most 20 — the same rule as `src/lib/checkout/trackbee.ts`. */
export const TRACKBEE_TS_PATTERN = /^\d{1,20}$/;

/** The create payload's top-level `trackbee` member. Every key is absent when unknown. */
export type PeakTrackBeeHint = {
  visitorId?: string;
  visitorTs?: string;
  consent?: { userData?: boolean; personalization?: boolean };
};

/** The slice of `localStorage` this module reads. Structural so it can be tested without a DOM. */
export type PeakLocalStorageLike = { getItem(key: string): string | null };

/** One key, never throwing; `null` when absent or unreadable. */
function read(storage: PeakLocalStorageLike | null | undefined, key: string): string | null {
  try {
    if (!storage) return null;
    const value = storage.getItem(key);
    return typeof value === 'string' ? value.trim() : null;
  } catch {
    return null;
  }
}

/** The visitor id and its timestamp, as far as they validate. The timestamp only with an id. */
export function readTrackBeeVisitor(storage: PeakLocalStorageLike | null | undefined): {
  visitorId?: string;
  visitorTs?: string;
} {
  const visitorId = read(storage, TRACKBEE_STORAGE_VISITOR_KEY);
  if (visitorId === null || !TRACKBEE_UUID_PATTERN.test(visitorId)) return {};
  const visitorTs = read(storage, TRACKBEE_STORAGE_TS_KEY);
  return visitorTs !== null && TRACKBEE_TS_PATTERN.test(visitorTs)
    ? { visitorId, visitorTs }
    : { visitorId };
}

/** Loose view of `window.Shopify.customerPrivacy`: the two methods that are asked. */
type PrivacyLike = { analyticsProcessingAllowed?: unknown; marketingAllowed?: unknown };

/** One Customer Privacy method: `undefined` when it is not on the page, `false` when it throws. */
function ask(api: PrivacyLike | null, method: keyof PrivacyLike): boolean | undefined {
  const fn = api ? api[method] : undefined;
  if (typeof fn !== 'function') return undefined;
  try {
    return (fn as () => unknown).call(api) === true;
  } catch {
    return false;
  }
}

/**
 * The shopper's consent in TrackBee's keys, from Shopify's Customer Privacy API when it is on the
 * page; `undefined` when neither method is. Never throws.
 */
export function readTrackBeeConsent(
  shopifyGlobal: unknown,
): { userData?: boolean; personalization?: boolean } | undefined {
  try {
    const api =
      shopifyGlobal && typeof shopifyGlobal === 'object'
        ? ((shopifyGlobal as { customerPrivacy?: unknown }).customerPrivacy as PrivacyLike | null)
        : null;
    const privacy = api && typeof api === 'object' ? api : null;
    const userData = ask(privacy, 'analyticsProcessingAllowed');
    const personalization = ask(privacy, 'marketingAllowed');
    if (userData === undefined && personalization === undefined) return undefined;
    const consent: { userData?: boolean; personalization?: boolean } = {};
    if (userData !== undefined) consent.userData = userData;
    if (personalization !== undefined) consent.personalization = personalization;
    return consent;
  } catch {
    return undefined;
  }
}

/**
 * The `trackbee` member for one handoff, or `undefined` when there is nothing to send. Never
 * throws: TrackBee data never stands between the customer and the checkout.
 */
export function readTrackBeeHint(
  storage: PeakLocalStorageLike | null | undefined,
  shopifyGlobal: unknown,
): PeakTrackBeeHint | undefined {
  const hint: PeakTrackBeeHint = readTrackBeeVisitor(storage);
  const consent = readTrackBeeConsent(shopifyGlobal);
  if (consent !== undefined) hint.consent = consent;
  return hint.visitorId !== undefined || hint.consent !== undefined ? hint : undefined;
}

/**
 * A URL with the visitor id as `_t_i` and `_t_i_ts` — for the one handoff that is a navigation
 * rather than a `POST`: a cart permalink taken over into a Peak buy link (`./buy-link.ts`), whose
 * route reads the two parameters like the create endpoint reads the `trackbee` member. Unchanged
 * when there is no visitor id or the URL does not parse.
 */
export function withTrackBeeVisitor(
  href: string,
  visitor: { visitorId?: string; visitorTs?: string },
): string {
  if (visitor.visitorId === undefined) return href;
  try {
    const url = new URL(href);
    url.searchParams.set('_t_i', visitor.visitorId);
    if (visitor.visitorTs !== undefined) url.searchParams.set('_t_i_ts', visitor.visitorTs);
    return url.toString();
  } catch {
    return href;
  }
}
